import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CURRENCY_NAME } from '@kopi-kaki/protocol';

/**
 * Starter Terms of Use and Privacy Policy for a Singapore-based hobby game.
 *
 * THIS IS A TEMPLATE, NOT LEGAL ADVICE. Review it (ideally with someone who
 * knows the PDPA), replace the placeholders marked YOUR-..., and keep it in
 * step with what the server actually stores and for how long.
 */

export type LegalDoc = 'terms' | 'privacy';

const CONTACT = 'admin@kopikaki.app';
const OPERATOR = 'Nuj Studios';
const UPDATED = '25 September 2026';

function Template({ children }: { children: ReactNode }) {
  return (
    <>
      <p className="legal-template">Template: to be reviewed by the operator before launch.</p>
      {children}
    </>
  );
}

function Terms() {
  return (
    <Template>
      <p className="muted">Last updated: {UPDATED}</p>
      <h3>1. Who can play</h3>
      <p>
        Kopi Kaki is a free social game run by {OPERATOR} in Singapore. You must be <b>13 or older</b> to play. If you
        are under 18, please get a parent or guardian's permission first.
      </p>
      <h3>2. Your account</h3>
      <p>
        Keep your password to yourself. Guest accounts are temporary and may be deleted after a period without play;
        save your guest as a full account to keep it.
      </p>
      <h3>3. Be a good kaki</h3>
      <ul>
        <li>No harassment, bullying, hate speech, threats or sexual content.</li>
        <li>Don't share personal details (addresses, phone numbers, school names), yours or anyone else's.</li>
        <li>No spam, scams, cheating, bots or exploiting bugs. Report bugs instead.</li>
        <li>No impersonating other players, moderators or real people.</li>
      </ul>
      <p>We may mute, suspend or delete accounts, rooms or content that break these rules.</p>
      <h3>4. {CURRENCY_NAME} and items</h3>
      <p>
        {CURRENCY_NAME} (K$) and in-game items are part of the game only. They have <b>no real-money value</b>, cannot be
        sold for real money, and cannot be exchanged for cash. We may change balances, prices or items to keep the
        game fair, for example after a bug or abuse.
      </p>
      <p>
        <b>Gold and Diamonds</b> are optional premium currencies for rare furniture, bigger room layouts and extra room
        slots. When buying is open, they can be bought with real money on a payment provider's own page. You can never
        earn them by playing and you never need them to play. Gold can be converted into {CURRENCY_NAME} and Diamonds
        into Gold, never the other way round. They have no value outside the game and cannot be cashed out, and selling
        or trading them for real money is not allowed. If you are under 18, get a parent or guardian's permission
        before buying anything.
      </p>
      <h3>5. Content you create</h3>
      <p>
        You keep ownership of what you write and design, and give us permission to show it in the game. Don't upload
        or create anything you don't have the right to share.
      </p>
      <h3>6. No warranty</h3>
      <p>
        The game is provided as is, may change or go offline, and may lose data. To the extent the law allows, we are
        not liable for losses from using it.
      </p>
      <h3>7. Contact</h3>
      <p>Questions? Email {CONTACT}.</p>
    </Template>
  );
}

function Privacy() {
  return (
    <Template>
      <p className="muted">Last updated: {UPDATED}</p>
      <p>
        This explains what personal data Kopi Kaki collects and how it is handled, in line with Singapore's Personal
        Data Protection Act (PDPA).
      </p>
      <h3>What we collect</h3>
      <ul>
        <li>Account details: username, display name, a securely hashed password (never the password itself).</li>
        <li>What you do in the game: chat messages, your avatar, motto, rooms, items and {CURRENCY_NAME} balance.</li>
        <li>Technical data: your IP address and browser details in server logs, and a session cookie that keeps you logged in.</li>
        <li>Reports you make about other players, and reports about you.</li>
        <li>If you buy Gold or Diamonds: a record of the purchase (the pack, the amount and the payment provider's reference). We never see or store your card details.</li>
      </ul>
      <h3>Why</h3>
      <p>
        To run the game, keep your account secure, stop abuse and cheating, moderate reported content, and fix bugs.
        We don't sell your data or use it for advertising.
      </p>
      <h3>How long we keep it</h3>
      <ul>
        <li>Chat messages: 30 days.</li>
        <li>Server logs (including IP addresses): 14 days.</li>
        <li>Inactive guest accounts: deleted after a period without play.</li>
        <li>Your account: until you delete it.</li>
      </ul>
      <h3>Your rights</h3>
      <ul>
        <li>
          <b>Access</b>: download a copy of your data from Account → Download my data.
        </li>
        <li>
          <b>Correction</b>: change your display name, look and motto in the game, or email us.
        </li>
        <li>
          <b>Deletion</b>: delete your account any time from Account → Delete account.
        </li>
        <li>
          <b>Withdraw consent</b>: stop playing and delete your account.
        </li>
      </ul>
      <h3>Children</h3>
      <p>
        Kopi Kaki is for players aged 13 and over. If you believe a child under 13 is using it, contact us and we will
        remove the account.
      </p>
      <h3>Contact</h3>
      <p>Data protection questions or requests: {CONTACT}.</p>
    </Template>
  );
}

export function LegalModal({ doc, onClose }: { doc: LegalDoc; onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portalled to <body> so no parent's stacking context can paint over it.
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="panel modal legal"
        role="dialog"
        aria-modal="true"
        aria-label={doc === 'terms' ? 'Terms of Use' : 'Privacy Policy'}
        onClick={(event) => event.stopPropagation()}
      >
        <h2>{doc === 'terms' ? 'Terms of Use' : 'Privacy Policy'}</h2>
        <div className="legal-body">{doc === 'terms' ? <Terms /> : <Privacy />}</div>
        <button className="btn btn-yellow" onClick={onClose} autoFocus>
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** The required "13+ and I agree" checkbox, with links that open the documents. */
export function TermsCheckbox({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  const [open, setOpen] = useState<LegalDoc | null>(null);
  const id = useId();
  return (
    <div className="terms-check">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} required />
      <label htmlFor={id}>
        I'm 13 or older and agree to the{' '}
        <button type="button" className="link-btn" onClick={() => setOpen('terms')}>
          Terms
        </button>{' '}
        and{' '}
        <button type="button" className="link-btn" onClick={() => setOpen('privacy')}>
          Privacy Policy
        </button>
        .
      </label>
      {open && <LegalModal doc={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Password input with a show/hide toggle. */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  minLength,
  enterKeyHint,
  hint,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  minLength?: number;
  enterKeyHint?: 'go' | 'next' | 'done';
  hint?: string;
  invalid?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <label>
      <span>
        {label} {hint && <small>{hint}</small>}
      </span>
      <span className="password-field">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          minLength={minLength}
          enterKeyHint={enterKeyHint}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={invalid}
          required
        />
        <button
          type="button"
          className="btn btn-small password-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </span>
    </label>
  );
}
